import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X, Ban, MailWarning, Send, Trash2 } from "lucide-react";
import { clsx } from "clsx";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUpdateUser } from "../../../hooks/useUsers";
import { useRemoveBusinessMember, useMemberMembershipCount } from "../../../hooks/useBusinesses";
import type { EmployeeProfile } from "../../../types/index";
import { roleApi, bizRoleApi } from "../../../lib/roleApi";
import { businessesApi } from "../../../lib/businessesApi";
import { usersApi } from "../../../lib/usersApi";
import { formatDate } from "../../../utils/format";

const DEPARTMENTS = [
  "Engineering",
  "HR",
  "Finance",
  "Operations",
  "Marketing",
  "IT",
  "Admin",
  "Health & Beauty",
] as const;

const WORK_DAYS = [
  { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" },
  { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" },
  { value: "friday", label: "Fri" },
  { value: "saturday", label: "Sat" },
  { value: "sunday", label: "Sun" },
];

const schema = z.object({
  firstName: z.string().min(1, "Required").max(100),
  lastName: z.string().min(1, "Required").max(100),
  email: z.string().email("Invalid email"),
  position: z.string().min(1, "Required").max(100),
  department: z.string().min(1, "Required").max(100),
  hireDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD"),
  baseSalary: z.preprocess(
    (val) => (typeof val === "number" && isNaN(val) ? undefined : val),
    z.number().optional()
  ),
  roleId: z.string().nullable().optional(),
  excludeFromPayroll: z.boolean().default(false),
  excludeFromGovDeductions: z.boolean().default(false),
  workDays: z.array(z.string()).optional(),
  hoursPerDay: z.preprocess(
    (val) => (typeof val === "number" && isNaN(val) ? undefined : val),
    z.number().optional()
  ),
});

type FormValues = z.infer<typeof schema>;

const INPUT_CLASS =
  "w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20";
const LABEL_CLASS = "block text-xs font-medium text-[#374151] mb-1";
const ERROR_CLASS = "text-[#dc2626] text-xs mt-1";

type AuditChanges = Record<string, { before?: unknown; after?: unknown }>;

function buildDiff(before: EmployeeProfile, after: FormValues): AuditChanges {
  const diff: AuditChanges = {};
  const simple: Array<{ label: string; b: unknown; a: unknown }> = [
    { label: "First Name",                   b: before.firstName,                   a: after.firstName },
    { label: "Last Name",                    b: before.lastName,                    a: after.lastName },
    { label: "Email",                        b: before.email,                       a: after.email },
    { label: "Position",                     b: before.position,                    a: after.position },
    { label: "Department",                   b: before.department,                  a: after.department },
    { label: "Hire Date",                    b: before.hireDate,                    a: after.hireDate },
    { label: "Base Salary",                  b: before.baseSalary,                  a: after.baseSalary },
    { label: "Access Role",                  b: before.roleId ?? null,              a: after.roleId ?? null },
    { label: "Exclude from Payroll",         b: before.excludeFromPayroll ?? false, a: after.excludeFromPayroll ?? false },
    { label: "Exclude from Gov. Deductions", b: before.excludeFromGovDeductions ?? false, a: after.excludeFromGovDeductions ?? false },
    { label: "Hours per Day",                b: before.workSchedule?.hoursPerDay,   a: after.hoursPerDay },
  ];
  for (const { label, b, a } of simple) {
    if (b !== a) diff[label] = { before: b ?? "—", after: a ?? "—" };
  }
  const bDays = [...(before.workSchedule?.days ?? [])].sort().join(", ");
  const aDays = [...(after.workDays ?? [])].sort().join(", ");
  if (bDays !== aDays) diff["Work Days"] = { before: bDays || "—", after: aDays || "—" };
  return diff;
}

interface EditUserDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: EmployeeProfile | null;
  onSuccess: (changes: AuditChanges) => void;
  businessId?: string;
}

export function EditUserDrawer({ isOpen, onClose, user, onSuccess, businessId }: EditUserDrawerProps) {
  const queryClient = useQueryClient();
  const isBizScope = !!businessId;
  const updateUser = useUpdateUser(user?.userId ?? "");
  const [resendStatus, setResendStatus] = useState<"idle" | "sending" | "sent" | "error" | "already_activated">("idle");

  const resendInvitation = useMutation({
    mutationFn: () => usersApi.resendInvitation(user!.userId),
    onMutate: () => setResendStatus("sending"),
    onSuccess: () => {
      setResendStatus("sent");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["biz-users", businessId] });
    },
    onError: (err: Error) => {
      if (err.message.includes("ACCOUNT_ALREADY_ACTIVATED")) {
        setResendStatus("already_activated");
      } else {
        setResendStatus("error");
      }
    },
  });

  const updateMembership = useMutation({
    mutationFn: (payload: { roleId: string | null; excludeFromPayroll: boolean }) =>
      businessesApi.updateMember(businessId!, user!.userId, payload),
    onSuccess: () => queryClient.refetchQueries({ queryKey: ["biz-users", businessId], type: "all" }),
  });

  const [confirmRemove, setConfirmRemove] = useState(false);
  const removeMembership = useRemoveBusinessMember(businessId ?? "");
  const { data: membershipCount } = useMemberMembershipCount(
    businessId ?? "",
    user?.userId ?? "",
    isBizScope && isOpen && !!user,
  );
  const isOnlyBusiness = membershipCount?.count === 1;
  const handleRemoveFromBusiness = async () => {
    if (!user) return;
    try {
      await removeMembership.mutateAsync(user.userId);
      queryClient.refetchQueries({ queryKey: ["biz-users", businessId], type: "all" });
      setConfirmRemove(false);
      onClose();
    } catch {
      // surfaced via removeMembership.isError below
    }
  };

  const { data: rolesData } = useQuery({
    queryKey: isBizScope ? ["biz-roles", businessId] : ["admin-roles"],
    queryFn: () => isBizScope ? bizRoleApi.list(businessId!) : roleApi.list(),
  });
  const roles = rolesData?.items ?? [];

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { workDays: [], excludeFromPayroll: false },
  });

  // Controlled watch for checkboxes — avoids React Hook Form uncontrolled ref
  // issues where reset() doesn't visually update checkboxes on iOS / drawer slide-in.
  const selectedDays = useWatch({ control, name: "workDays" }) ?? [];

  // Reset transient state when switching users
  useEffect(() => {
    setResendStatus("idle");
    setConfirmRemove(false);
  }, [user?.userId]);

  // Pre-fill when user changes
  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        position: user.position,
        department: user.department,
        hireDate: user.hireDate,
        baseSalary: user.baseSalary,
        roleId: user.roleId ?? null,
        excludeFromPayroll: user.excludeFromPayroll ?? false,
        excludeFromGovDeductions: user.excludeFromGovDeductions ?? false,
        workDays: user.workSchedule?.days ?? [],
        hoursPerDay: user.workSchedule?.hoursPerDay,
      });
    }
  }, [user, reset]);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const onSubmit = async (data: FormValues) => {
    const workSchedule =
      data.workDays && data.workDays.length > 0 && data.hoursPerDay
        ? { days: data.workDays, hoursPerDay: data.hoursPerDay }
        : undefined;

    if (isBizScope) {
      // In biz context: profile fields go to the global profile; roleId and the
      // per-business payroll exclusion go to this business's membership.
      await Promise.all([
        updateUser.mutateAsync({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          position: data.position,
          department: data.department,
          hireDate: data.hireDate,
          baseSalary: data.baseSalary,
          excludeFromGovDeductions: data.excludeFromGovDeductions,
          workSchedule,
        }),
        updateMembership.mutateAsync({
          roleId: data.roleId ?? null,
          excludeFromPayroll: data.excludeFromPayroll ?? false,
        }),
      ]);
    } else {
      await updateUser.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        position: data.position,
        department: data.department,
        hireDate: data.hireDate,
        baseSalary: data.baseSalary,
        excludeFromPayroll: data.excludeFromPayroll,
        excludeFromGovDeductions: data.excludeFromGovDeductions,
        roleId: data.roleId ?? null,
        workSchedule,
      });
    }

    onSuccess(user ? buildDiff(user, data) : {});
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={clsx(
          "fixed inset-0 z-40 bg-black/30 transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        className={clsx(
          "fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-xl flex flex-col",
          "transform transition-transform duration-300",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Edit Employee"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-[#0f172a]">Edit Employee</h2>
            {user && (
              <p className="text-xs text-[#64748b] mt-0.5">
                @{user.username}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center h-8 w-8 rounded-lg text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] transition-colors"
            aria-label="Close drawer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Amber warning — only when Cognito failed to deliver on original creation */}
          {user && user.invitationEmailSent === false && resendStatus === "idle" && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
              <MailWarning className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-800">Invitation email not sent</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Cognito failed to deliver the invitation email to{" "}
                  <span className="font-medium">{user.email}</span> when this account was created.
                </p>
              </div>
            </div>
          )}

          {/* Resend invitation — always available */}
          {user && resendStatus !== "sent" && (
            <div className="mb-4 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 min-w-0">
                <Send className="h-4 w-4 text-[#64748b] shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#374151]">Invitation Email</p>
                  {resendStatus === "already_activated" && (
                    <p className="text-xs text-[#64748b] mt-0.5">
                      This account has already been activated — no invitation can be resent.
                    </p>
                  )}
                  {resendStatus === "error" && (
                    <p className="text-xs text-[#dc2626] mt-0.5">
                      Failed to send. Check that the email address is valid and try again.
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                disabled={resendStatus === "sending" || resendStatus === "already_activated"}
                onClick={() => resendInvitation.mutate()}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-[#e2e8f0] bg-white px-3 py-1.5 text-xs font-medium text-[#374151] hover:bg-[#f1f5f9] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="h-3 w-3" />
                {resendStatus === "sending" ? "Sending…" : "Resend invitation"}
              </button>
            </div>
          )}

          {resendStatus === "sent" && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 flex items-center gap-2">
              <Send className="h-4 w-4 text-green-600 shrink-0" />
              <p className="text-sm text-green-800">Invitation email resent successfully.</p>
            </div>
          )}

          <form id="edit-user-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">

            {/* Name row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL_CLASS}>First Name</label>
                <input {...register("firstName")} className={INPUT_CLASS} />
                {errors.firstName && <p className={ERROR_CLASS}>{errors.firstName.message}</p>}
              </div>
              <div>
                <label className={LABEL_CLASS}>Last Name</label>
                <input {...register("lastName")} className={INPUT_CLASS} />
                {errors.lastName && <p className={ERROR_CLASS}>{errors.lastName.message}</p>}
              </div>
            </div>

            {/* Username (read-only) */}
            <div>
              <label className={LABEL_CLASS}>
                Username <span className="text-[#94a3b8] font-normal">(cannot be changed)</span>
              </label>
              <div className="w-full rounded-lg border border-[#e2e8f0] bg-[#f1f5f9] px-3 py-2 text-sm text-[#64748b] select-all">
                {user?.username ?? "—"}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className={LABEL_CLASS}>Email Address</label>
              <input {...register("email")} type="email" className={INPUT_CLASS} />
              {errors.email && <p className={ERROR_CLASS}>{errors.email.message}</p>}
            </div>

            {/* Position */}
            <div>
              <label className={LABEL_CLASS}>Position</label>
              <input {...register("position")} className={INPUT_CLASS} placeholder="e.g. Hair Stylist" />
              {errors.position && <p className={ERROR_CLASS}>{errors.position.message}</p>}
            </div>

            {/* Department */}
            <div>
              <label className={LABEL_CLASS}>Department</label>
              <select {...register("department")} className={INPUT_CLASS}>
                <option value="">Select department</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              {errors.department && <p className={ERROR_CLASS}>{errors.department.message}</p>}
            </div>

            {/* Hire Date + Base Salary */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL_CLASS}>Hire Date</label>
                <input {...register("hireDate")} type="date" className={INPUT_CLASS} />
                {errors.hireDate && <p className={ERROR_CLASS}>{errors.hireDate.message}</p>}
              </div>
              <div>
                <label className={LABEL_CLASS}>Base Salary (₱) <span className="text-[#94a3b8] font-normal">(optional)</span></label>
                <input
                  {...register("baseSalary", { valueAsNumber: true })}
                  type="number" min="0" step="0.01"
                  className={INPUT_CLASS}
                />
                {errors.baseSalary && <p className={ERROR_CLASS}>{errors.baseSalary.message}</p>}
              </div>
            </div>

            {/* Cognito role — read-only (changes are manual via Cognito console) */}
            <div>
              <label className={LABEL_CLASS}>Account Type</label>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  user?.role === "super-admin"
                    ? "bg-[#dbeafe] text-[#1e40af]"
                    : "bg-[#f0fdf4] text-[#166534]"
                }`}>
                  {user?.role === "super-admin" ? "Super Admin" : "Employee"}
                </span>
                <span className="text-[11px] text-[#94a3b8]">
                  (managed via Cognito console)
                </span>
              </div>
            </div>

            {/* Access Role */}
            <div>
              <label className={LABEL_CLASS}>
                {isBizScope ? "Business Access Role" : "Access Role"}{" "}
                <span className="text-[#94a3b8] font-normal">(optional)</span>
              </label>
              <select {...register("roleId")} className={INPUT_CLASS}>
                <option value="">— No role (basic employee access) —</option>
                {roles.map((r) => (
                  <option key={r.roleId} value={r.roleId}>
                    {r.name}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-[#94a3b8] mt-1">
                {isBizScope
                  ? "Controls which pages this employee can access within this business."
                  : "Controls which pages this employee can access. Leave blank to allow all pages."}
              </p>
              {errors.roleId && <p className={ERROR_CLASS}>{errors.roleId.message}</p>}
            </div>

            {/* Exclude from payroll */}
            <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3">
              <input
                type="checkbox"
                {...register("excludeFromPayroll")}
                className="mt-0.5 h-4 w-4 rounded border-[#e2e8f0] accent-[#dc2626]"
              />
              <div>
                <div className="flex items-center gap-1.5">
                  <Ban className="h-3.5 w-3.5 text-[#dc2626]" />
                  <span className="text-sm font-medium text-[#0f172a]">Exclude from payroll</span>
                </div>
                <p className="text-xs text-[#64748b] mt-0.5">
                  {isBizScope
                    ? "Skips this employee during THIS business's batch payroll only — their payroll in other businesses is unaffected."
                    : "This employee will be skipped during batch payroll processing (e.g. owner, admin-only accounts)."}
                </p>
              </div>
            </label>

            {/* Exclude from government deductions */}
            <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3">
              <input
                type="checkbox"
                {...register("excludeFromGovDeductions")}
                className="mt-0.5 h-4 w-4 rounded border-[#e2e8f0] accent-[#d97706]"
              />
              <div>
                <div className="flex items-center gap-1.5">
                  <Ban className="h-3.5 w-3.5 text-[#d97706]" />
                  <span className="text-sm font-medium text-[#0f172a]">Exclude from government deductions</span>
                </div>
                <p className="text-xs text-[#64748b] mt-0.5">
                  SSS, PhilHealth, and Pag-IBIG will not be deducted from this employee's payslip (e.g. contractual, project-based).
                </p>
              </div>
            </label>

            {/* Work Schedule */}
            <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-4 space-y-3">
              <p className="text-xs font-semibold text-[#374151] uppercase tracking-wide">
                Work Schedule <span className="text-[#94a3b8] normal-case font-normal">(optional)</span>
              </p>
              <div>
                <p className="text-xs font-medium text-[#374151] mb-2">Working Days</p>
                <div className="flex flex-wrap gap-2">
                  {WORK_DAYS.map(({ value, label }) => (
                    <label key={value} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDays.includes(value)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...selectedDays, value]
                            : selectedDays.filter((d: string) => d !== value);
                          setValue("workDays", next, { shouldValidate: true });
                        }}
                        className="h-3.5 w-3.5 rounded border-[#e2e8f0] accent-[#2447d9]"
                      />
                      <span className="text-xs text-[#374151]">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className={LABEL_CLASS}>Expected Hours per Day</label>
                <input
                  {...register("hoursPerDay", { valueAsNumber: true })}
                  type="number" step="0.5"
                  placeholder="e.g. 8"
                  className="w-32 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
                />
                {errors.hoursPerDay && <p className={ERROR_CLASS}>{errors.hoursPerDay.message}</p>}
              </div>
            </div>

            {/* Audit trail */}
            {user && (
              <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 space-y-2">
                <p className="text-xs font-semibold text-[#374151] uppercase tracking-wide">Audit Trail</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div>
                    <p className="text-[#94a3b8]">Created By</p>
                    <p className="font-medium text-[#374151]">{user.createdBy ?? "—"}</p>
                  </div>
                  <div>
                    <p className="text-[#94a3b8]">Created On</p>
                    <p className="font-medium text-[#374151]">{user.createdAt ? formatDate(user.createdAt) : "—"}</p>
                  </div>
                  <div>
                    <p className="text-[#94a3b8]">Last Updated By</p>
                    <p className="font-medium text-[#374151]">{user.updatedBy ?? "—"}</p>
                  </div>
                  <div>
                    <p className="text-[#94a3b8]">Last Updated On</p>
                    <p className="font-medium text-[#374151]">{user.updatedAt ? formatDate(user.updatedAt) : "—"}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Danger zone — remove from this business (biz scope only) */}
            {isBizScope && (
              <div className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-4 py-3">
                <p className="text-xs font-semibold text-[#b91c1c] uppercase tracking-wide mb-1">Danger Zone</p>
                <p className="text-xs text-[#7f1d1d] mb-3">
                  Remove this employee from this business. They lose access to it and it disappears
                  from their business switcher. Their account and other businesses are unaffected.
                </p>

                {/* Only-business recommendation */}
                {isOnlyBusiness && (
                  <div className="mb-3 rounded-lg border border-[#fcd34d] bg-[#fffbeb] px-3 py-2">
                    <p className="text-xs font-medium text-[#92400e]">
                      This is their only business.
                    </p>
                    <p className="text-[11px] text-[#b45309] mt-0.5">
                      Removing it leaves the account with no business and no access (they can still
                      sign in but won&apos;t see anything). To fully offboard them, use
                      <strong> Deactivate</strong> in Manage Users instead.
                    </p>
                  </div>
                )}

                {confirmRemove ? (
                  <div className="space-y-2">
                    <p className="text-xs text-[#7f1d1d]">
                      This removes their membership in this business only
                      {isOnlyBusiness ? " — their last one" : ""}. It does not delete their account.
                      Continue?
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleRemoveFromBusiness}
                        disabled={removeMembership.isPending}
                        className="rounded-lg bg-[#dc2626] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#b91c1c] disabled:opacity-60 transition-colors"
                      >
                        {removeMembership.isPending ? "Removing…" : "Yes, remove membership"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmRemove(false)}
                        disabled={removeMembership.isPending}
                        className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-1.5 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmRemove(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#dc2626] px-3 py-1.5 text-sm font-medium text-[#dc2626] hover:bg-[#fee2e2] transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Remove from this business
                  </button>
                )}
                {removeMembership.isError && (
                  <p className="mt-2 text-xs text-[#dc2626]">Failed to remove from business. Please try again.</p>
                )}
              </div>
            )}

            {(updateUser.isError || updateMembership.isError) && (
              <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">
                Failed to update employee. Please try again.
              </p>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="edit-user-form"
            disabled={isSubmitting}
            className="rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </>
  );
}
